/**
 * Model Configuration
 * Maps each BIM model to its URN and associated compliance report files.
 */

export interface ModelReportSet {
  type: string;
  files: string[];
}

export interface JurisdictionOverride {
  jurisdiction: string;
  reportSets: ModelReportSet[];
  regulationDefinitionFiles?: {
    ada604?: string;
    ada404?: string;
    generic?: string;
  };
}

export interface ModelConfig {
  id: string;
  name: string;
  /** Display name for use in PDF reports (e.g., "Ritz-Sagamore r21 Tower Residential") */
  projectName?: string;
  urn: string;
  reportsFolder: string;
  /** Default building level when reports don't include a level field */
  defaultLevel?: string;
  /** Jurisdiction label for PDF reports (e.g., "Florida — ADA 2010 + FBC 2023 apply") */
  jurisdiction?: string;
  /** BIM model name (e.g., Revit file name) shown on PDF cover page */
  modelName?: string;
  /** Path to a logo image (relative to /resources/) shown on PDF cover page */
  coverLogoPath?: string;
  /** Path to a BIM model image (relative to /resources/) shown on PDF cover page below logo */
  coverImagePath?: string;
  reportSets: ModelReportSet[];
  /** Regulation definition files (e.g., ada_604_jersey.json, ADA_404_doors.json) */
  regulationDefinitionFiles?: {
    ada604?: string;
    ada404?: string;
    /** Generic definitions file keyed by regulation_id */
    generic?: string;
  };
  /** Per-jurisdiction overrides for reportSets and regulation files */
  jurisdictionOverrides?: Record<string, JurisdictionOverride>;
  /** Pre-populated team members for this model */
  teamMembers?: Array<{ name: string; email: string; role: string }>;
}

export const MODEL_CONFIGS: ModelConfig[] = [
  {
    id: 'skylight',
    name: 'Skylight',
    projectName: 'Skylight',
    jurisdiction: 'New York',
    urn: 'urn:dXJuOmFkc2sud2lwcHJvZDpmcy5maWxlOnZmLmphMGZxWGtqUnFxOUNndmpPNTc5c1E_dmVyc2lvbj0z',
    reportsFolder: 'results/skylight',
    reportSets: [
      {
        type: 'skylight_sot',
        files: [
          'results/skylight/skylight_all_units_sot.json',
        ],
      },
    ],
    teamMembers: [
      { name: "Jeremy Tabora", email: "jtabora@skylightrep.com", role: "viewer" },
      { name: "Tamir Levy", email: "tamirl@herocon.ai", role: "support_team" },
    ],
  },
  {
    id: 'dutch-bros',
    name: 'Dutch Bros',
    projectName: 'Cole Valley Partners',
    urn: import.meta.env.VITE_DEFAULT_MODEL_URN1 || '',
    reportsFolder: 'results/dutch-bros',
    defaultLevel: 'LEVEL 1',
    jurisdiction: 'Texas — ICC A117.1 · TAS 2012 · ADA 2010',
    modelName: 'A1 Annihilator',
    coverLogoPath: 'results/dutch-bros/cvpre_logo.jpeg',
    coverImagePath: 'results/dutch-bros/cvp.png',
    reportSets: [
      {
        type: 'scan',
        files: [
          'results/dutch-bros/chapter6_scan_bathroom_validated_enriched_v2.json',
        ],
      },
      {
        type: 'cloud_scan',
        files: [
          'results/dutch-bros/scan_results_404_cloud_final_20260312_enriched.json',
        ],
      },
      {
        type: 'plumbing',
        files: [
          'results/dutch-bros/plumbing_regulations.json',
        ],
      },
    ],
    regulationDefinitionFiles: {
      generic: 'results/dutch-bros/icc_a117_regulation_definitions.json',
    },
    jurisdictionOverrides: {
      'Florida': {
        jurisdiction: 'Florida — FBC 2023 + ADA 2010',
        reportSets: [
          {
            type: 'scan',
            files: [
              'results/dutch-bros/florida/chapter6_scan_bathroom_validated_enriched_v2.json',
            ],
          },
          {
            type: 'cloud_scan',
            files: [
              'results/dutch-bros/florida/scan_results_404_cloud_final_enriched.json',
            ],
          },
        ],
        regulationDefinitionFiles: {
          generic: 'results/dutch-bros/florida/fbc_2023_regulation_definitions.json',
        },
      },
    },
    teamMembers: [
      { name: "Matteo Gallo", email: "matteo.gallo@cvpre.com", role: "partner" },
      { name: "Braden Bernards", email: "braden.bernards@cvpre.com", role: "vice_president" },
      { name: "Paul Deppe", email: "deppe@adnarch.com", role: "architect" },
    ],
  },
  {
    id: 'bne',
    name: 'BNE',
    projectName: 'Multi-Family Residential, Hillsdale',
    urn: import.meta.env.VITE_DEFAULT_MODEL_URN || '',
    reportsFolder: 'results/bne',
    defaultLevel: 'LEVEL 2 T.O. GYPSUM',
    jurisdiction: 'New Jersey — ICC A117.1-2017',
    reportSets: [
      {
        type: 'toilet',
        files: [
          'results/bne/604/240167_3BR-AU_v16_validated.json',
          'results/bne/604/240168_3BR-AU_v16_validated.json',
          'results/bne/604/240169_3BR-AU_v16_validated.json',
          'results/bne/604/240170_3BR-AU_v16_validated.json',
          'results/bne/604/240190_2BR-AU_v16_validated.json',
          'results/bne/604/240191_2BR-AU_v16_validated.json',
          'results/bne/604/240192_2BR-AU_v16_validated.json',
          'results/bne/604/240193_2BR-AU_v16_validated.json',
          'results/bne/604/240194_2BR-AU_v16_validated.json',
          'results/bne/604/240195_2BR-AU_v16_validated.json',
          'results/bne/604/275317_2BR-AU_v16_validated.json',
          'results/bne/604/275429_2BR-AU_v16_validated.json',
          'results/bne/604/275452_2BR-AU_v16_validated.json',
          'results/bne/604/391394_AU-1BR_v16_validated.json',
          'results/bne/604/391560_AU-1BR_v16_validated.json',
          'results/bne/604/391723_AU-1BR_v16_validated.json',
          'results/bne/604/391859_AU-1BR_v16_validated.json',
          'results/bne/604/404924_2BR-AU_v16_validated.json',
          'results/bne/604/404925_2BR-AU_v16_validated.json',
        ],
      },
      {
        type: 'door',

        files: [
          'results/bne/404/door_113035_20260106_183956.json',
          'results/bne/404/door_113036_20260106_183958.json',
          'results/bne/404/door_155855_20260106_184002.json',
          'results/bne/404/door_170728_20260106_184004.json',
          'results/bne/404/door_181775_20260106_184006.json',
          'results/bne/404/door_301859_20260106_184008.json',
          'results/bne/404/door_391372_20260106_184009.json',
          'results/bne/404/door_391373_20260106_184010.json',
          'results/bne/404/door_391382_20260106_184011.json',
          'results/bne/404/door_462741_20260106_184013.json',
        ],
      },
    ],
    regulationDefinitionFiles: {
      ada604: 'results/bne/ada_604_jersey.json',
      ada404: 'results/bne/ADA_404_doors.json',
    },
  },
];

export const getModelConfig = (id: string): ModelConfig | undefined =>
  MODEL_CONFIGS.find((m) => m.id === id);

export const getDefaultModelConfig = (): ModelConfig => MODEL_CONFIGS[0];

export const getModelRegulationDefinitionFiles = (
  modelId: string,
  jurisdiction?: string | null
): { ada604?: string; ada404?: string; generic?: string } => {
  const model = jurisdiction
    ? getModelConfigForJurisdiction(modelId, jurisdiction)
    : getModelConfig(modelId);
  return model?.regulationDefinitionFiles || {};
};

export const getModelReportFiles = (
  modelId: string,
  reportType?: string
): string[] => {
  const model = getModelConfig(modelId);
  if (!model) return [];
  const sets = reportType
    ? model.reportSets.filter((s) => s.type === reportType)
    : model.reportSets;
  return sets.flatMap((s) => s.files);
};

/**
 * Resolve model config with jurisdiction overrides applied.
 * If jurisdiction matches a key in jurisdictionOverrides, reportSets and
 * regulationDefinitionFiles are replaced with the override values.
 */
export const getModelConfigForJurisdiction = (
  modelId: string,
  jurisdiction?: string | null
): ModelConfig | undefined => {
  const model = getModelConfig(modelId);
  if (!model) return undefined;

  // Find matching jurisdiction override by checking if the jurisdiction string contains the key
  if (jurisdiction && model.jurisdictionOverrides) {
    for (const [key, override] of Object.entries(model.jurisdictionOverrides)) {
      if (jurisdiction.toLowerCase().includes(key.toLowerCase())) {
        return {
          ...model,
          jurisdiction: override.jurisdiction,
          reportSets: override.reportSets,
          regulationDefinitionFiles: override.regulationDefinitionFiles ?? model.regulationDefinitionFiles,
        };
      }
    }
  }

  return model;
};

export const getModelReportFilesForJurisdiction = (
  modelId: string,
  jurisdiction?: string | null,
  reportType?: string
): string[] => {
  const model = getModelConfigForJurisdiction(modelId, jurisdiction);
  if (!model) return [];
  const sets = reportType
    ? model.reportSets.filter((s) => s.type === reportType)
    : model.reportSets;
  return sets.flatMap((s) => s.files);
};
